import "core-js/modules/es.iterator.constructor.js";
import "core-js/modules/es.iterator.some.js";
import { shallowRef } from 'vue';
import i18n from '@/plugins/i18n';
import { ElMessage } from 'element-plus';
import { useStore } from 'vuex';
import ConfirmDialog from '@/components/memberManager/confirmDialog.vue';
import { useAuthTip } from '@/common/hooks/useAuthTip';

/**
 * 显示牛币消耗结果的轻提示（ElMessage）。
 * @param {Object} params
 * @param {number} params.memberType - 会员类型，0 表示权益已耗尽
 * @param {number} params.needPayNum - 本次消耗的牛币数量
 * @param {number} params.remainingNum - 剩余牛币数量
 * @param {string} [params.message] - 自定义消息内容（传入后覆盖默认文案）
 */
export function nbiNumberMessage(_ref) {
  let _ref$memberType = _ref.memberType,
    memberType = _ref$memberType === void 0 ? 1 : _ref$memberType,
    _ref$needPayNum = _ref.needPayNum,
    needPayNum = _ref$needPayNum === void 0 ? 0 : _ref$needPayNum,
    _ref$remainingNum = _ref.remainingNum,
    remainingNum = _ref$remainingNum === void 0 ? 0 : _ref$remainingNum,
    message = _ref.message;
  if (needPayNum === 0) return;
  ElMessage({
    message: message || `<p style="color: #565960;">${memberType === 0 ? i18n.global.t('member.payment.nbiExhaustedMsg', {
      needPayNum,
      remainingNum
    }) : i18n.global.t('member.payment.nbiConsumeMsg', {
      needPayNum,
      remainingNum
    })}</p>`,
    icon: '',
    customClass: 'custom-wallet-tips',
    dangerouslyUseHTMLString: true
  });
}

/**
 * 弹出牛币余额不足的确认弹窗（globalDialog），引导用户充值。
 * @param {Object} params
 * @param {number} [params.needPayNum=50] - 本次需消耗的牛币数量
 * @param {number} [params.remainingNum=0] - 当前剩余牛币数量
 * @param {number} [params.messageType=0] - 消息类型
 *   /**
 * 通用文案类型 0
 * AI分身批量添加文案类型 1
 * @param {Object} [params.store=store] - 存储实例
 * @returns {Promise<void>}
 */
export function nbiConfirmDialog(_ref2, store) {
  let _ref2$needPayNum = _ref2.needPayNum,
    needPayNum = _ref2$needPayNum === void 0 ? 0 : _ref2$needPayNum,
    _ref2$remainingNum = _ref2.remainingNum,
    remainingNum = _ref2$remainingNum === void 0 ? 0 : _ref2$remainingNum,
    _ref2$messageType = _ref2.messageType,
    messageType = _ref2$messageType === void 0 ? 0 : _ref2$messageType;
  const NBI_DIALOG_ID = 'nbi-confirm-dialog-guide';
  const isExist = store.state.globalDialog.list.some(item => item.id === NBI_DIALOG_ID);
  if (isExist) return;
  store.commit('globalDialog/addDialog', {
    props: {
      id: NBI_DIALOG_ID
    },
    component: shallowRef(ConfirmDialog),
    customClass: '',
    propsData: {
      width: '480px',
      needPayNum,
      remainingNum,
      messageType,
      headerRender: h => {
        return h('div', {
          style: 'display:flex;align-items:center;'
        }, [h('i', {
          style: 'font-size:20px;margin-right:8px;color:#E53939;',
          class: 'icon iconfont icon-jinggao'
        }), h('span', {
          style: 'font-size:16px;font-weight:600;line-height:normal;',
          class: 'title text'
        }, i18n.global.t('member.payment.insufficientNbi'))]);
      },
      showFooter: false
    },
    methods: {}
  });
}
let pendingNbiCallback = null;

/**
 * 注册牛币确认弹窗回调
 * @param {Function} callback - 回调函数
 */
export function registerNbiConfirmDialog(callback) {
  pendingNbiCallback = callback;
}

/**
 * 刷新牛币确认弹窗
 */
export function flushNbiConfirmDialog() {
  if (pendingNbiCallback) {
    pendingNbiCallback();
    pendingNbiCallback = null;
  }
}

/**
 * 牛币消费相关的组合式函数，提供牛币消耗提示和余额不足确认弹窗能力。
 * 需在 Vue setup 上下文中调用（依赖 useStore）。
 * @returns {Object} result
 * @returns {Function} result.aiAvatarMemberGuide - AI头像会员引导
 * @returns {Function} result.recordMeetingMemberGuide - 会议纪要会员引导
 * @returns {Function} result.nbiNumberMessage - 显示牛币消耗轻提示
 * @returns {Function} result.nbiConfirmDialog - 弹出牛币不足确认弹窗
 * @returns {Function} result.showMemberTip - 根据会员类型展示对应的牛币提示，内部调用 nbiConfirmDialog 或 nbiNumberMessage
 */
export function useMember() {
  const innerStore = useStore();

  /**
   * 根据会员类型展示对应的牛币提示。
   * - isExhausted === false ElMessage 轻提示
   * - isExhausted === true 弹出确认弹窗
   *
   * 内部调用 useMember()，无需在 setup 外额外初始化。
   * @param {Object} params
   * @param {boolean} [params.isExhausted=false] - 是否是余额不足
   * @param {number} [params.memberType=1] - 会员类型，0会员权益额度消耗完毕 1，现体验/专业/机构会员:消耗提示文案中包含消耗/剩余信息
   * @param {number} [params.needPayNum=10] - 本次需消耗的牛币数量
   * @param {number} [params.remainingNum=10] - 当前剩余牛币数量
   */
  function showMemberTip(_ref3) {
    let _ref3$isExhausted = _ref3.isExhausted,
      isExhausted = _ref3$isExhausted === void 0 ? false : _ref3$isExhausted,
      _ref3$memberType = _ref3.memberType,
      memberType = _ref3$memberType === void 0 ? 1 : _ref3$memberType,
      _ref3$needPayNum = _ref3.needPayNum,
      needPayNum = _ref3$needPayNum === void 0 ? 0 : _ref3$needPayNum,
      _ref3$remainingNum = _ref3.remainingNum,
      remainingNum = _ref3$remainingNum === void 0 ? 0 : _ref3$remainingNum,
      _ref3$messageType = _ref3.messageType,
      messageType = _ref3$messageType === void 0 ? 0 : _ref3$messageType;
    if (isExhausted) {
      // 余额不足，弹出确认弹窗
      nbiConfirmDialog({
        needPayNum,
        remainingNum,
        messageType
      }, innerStore);
    } else {
      // 消耗提示文案中包含消耗/剩余信息，显示轻提示
      nbiNumberMessage({
        memberType,
        needPayNum,
        remainingNum
      });
    }
  }

  /**
   * AI 头像会员引导
   * @param {{ openPayOnNoAuth?: boolean }} [options] - 无权益时是否自动打开支付弹窗
   * @returns {Promise<{ haveAuth: boolean, vipBenefit: Object }>} 权益状态与会员权益信息
   */
  async function aiAvatarMemberGuide() {
    let _ref4 = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {},
      _ref4$openPayOnNoAuth = _ref4.openPayOnNoAuth,
      openPayOnNoAuth = _ref4$openPayOnNoAuth === void 0 ? true : _ref4$openPayOnNoAuth;
    const _await$useAuthTip = await useAuthTip('ai_will_remember'),
      haveAuth = _await$useAuthTip.haveAuth,
      vipBenefit = _await$useAuthTip.vipBenefit;
    // 没有权益，打开支付弹窗
    if (!haveAuth.value && openPayOnNoAuth) {
      innerStore.commit('setWebPayDialog', {
        visible: true
      });
    }
    return {
      haveAuth: haveAuth.value,
      vipBenefit
    };
  }

  /**
   * 会议纪要会员引导
   * @returns {Promise<boolean>} true 可以继续后续逻辑，false 需要拦截
   */
  async function recordMeetingMemberGuide() {
    const _await$useAuthTip2 = await useAuthTip('slef_help_meet'),
      haveAuth = _await$useAuthTip2.haveAuth;
    // 没有权益，打开支付弹窗
    if (!haveAuth.value) {
      innerStore.commit('setWebPayDialog', {
        visible: true
      });
    }
    return haveAuth.value;
  }
  return {
    aiAvatarMemberGuide,
    recordMeetingMemberGuide,
    nbiNumberMessage,
    nbiConfirmDialog: _ref5 => {
      let needPayNum = _ref5.needPayNum,
        remainingNum = _ref5.remainingNum,
        messageType = _ref5.messageType;
      return nbiConfirmDialog({
        needPayNum,
        remainingNum,
        messageType
      }, innerStore);
    },
    showMemberTip
  };
}